
import React, { useEffect, useMemo, useState } from "react";

import axios from "../../helpers/axios";
import { generatePublicUrl } from "../../urlConfig";
import { ChatDialog } from "./ChatDialogs";
import "../../scss/components/_chat-product-card.scss";

/*
 * Карточка товара из чата: фото, цены по камням (подписка и обычная),
 * размеры, описание. Только то, что можно показать клиенту: без цены
 * изготовления, фрезеровки и заготовки. Сервер: GET /admin/product/:id/card.
 */

const money = (value) => {
    const number = Number(value || 0);
    return number > 0 ? `${Math.round(number).toLocaleString("ru-RU")} ₽` : "";
};

const imageSrc = (value) => {
    const image = String(value || "");
    if (!image) return "";
    if (/^(https?:)?\/\//i.test(image) || /^(blob:|data:image\/)/i.test(image) || image.startsWith("/media/")) return image;
    return generatePublicUrl(image);
};

const sizeText = (item) => [item?.height, item?.width, item?.thickness]
    .map((value) => Number(value || 0))
    .filter((value) => value > 0)
    .join(" × ");

/**
 * Цены по камням: «Габбро — подписка 44 242 ₽, обычная 53 156 ₽».
 * Сервер присылает их списком stonePrices; старый сервер — только по
 * материалам товара (materials) или общие цены (pricing).
 */
export const getStonePrices = (product = {}) => {
    const fromServer = Array.isArray(product.stonePrices) ? product.stonePrices : [];
    const fromMaterials = Array.isArray(product.materials) ? product.materials : [];
    const source = fromServer.length
        ? fromServer
        : fromMaterials.length
            ? fromMaterials
            : product.pricing ? [{ ...product.pricing, name: product.pricing.materialName || product.materialName || "" }] : [];

    return source
        .map((item) => ({
            key: String(item.key || item.materialKey || item.name || ""),
            name: String(item.name || item.materialName || "").trim(),
            subscriberPrice: Number(item.subscriberPrice || 0),
            publicPrice: Number(item.publicPrice || 0),
            // Фрезеровка отдельно: её можно заказать и без камня.
            millingPrice: Number(item.millingPrice || 0),
            note: String(item.note || ""),
        }))
        .filter((item) => item.subscriberPrice > 0 || item.publicPrice > 0 || item.millingPrice > 0);
};

export default function ProductCardModal({ productId, title = "", onClose }) {
    const [state, setState] = useState({ loading: true, error: "", product: null });
    const [photo, setPhoto] = useState(0);

    useEffect(() => {
        let cancelled = false;
        setState({ loading: true, error: "", product: null });

        axios.get(`/admin/product/${encodeURIComponent(productId)}/card`)
            .then(({ data }) => {
                if (cancelled) return;
                const product = data?.product || null;
                setState({ loading: false, error: product ? "" : "Товар не найден", product });
                setPhoto(0);
            })
            .catch((error) => {
                if (cancelled) return;
                const status = Number(error?.response?.status || 0);
                setState({
                    loading: false,
                    error: status === 404
                        ? "Товар удалён или больше недоступен."
                        : status === 403 ? "Нет доступа к этому товару." : "Не удалось открыть карточку товара.",
                    product: null,
                });
            });

        return () => {
            cancelled = true;
        };
    }, [productId]);

    const product = state.product;

    const pictures = useMemo(() => (product?.pictures || [])
        .filter((item) => item?.img && item.visibleImg !== false)
        .map((item) => imageSrc(item.img)), [product]);

    const stones = useMemo(() => (product ? getStonePrices(product) : []), [product]);

    // Размеры — из общих цен товара (у всех камней размеры одни).
    const sizes = useMemo(() => (product?.pricing?.sizes || [])
        .map((item) => item.label || sizeText(item))
        .filter(Boolean), [product]);

    const facts = product ? [
        product.article != null ? ["Артикул", product.article] : null,
        product.categoryName ? ["Категория", product.categoryName] : null,
        product.size ? ["Размер", product.size] : null,
        product.weight ? ["Вес", product.weight] : null,
        product.has3D ? ["3D-модель", "Есть"] : null,
    ].filter(Boolean) : [];

    const name = product?.fullName || product?.name || title || "Товар";

    return (
        <ChatDialog title={name} onClose={onClose} wide>
            {state.loading ? <div className="chat-dialog-AO__hint">Загружаем карточку товара…</div> : null}
            {state.error ? <div className="chat-dialog-AO__error" role="alert">{state.error}</div> : null}

            {product ? (
                <div className="chat-product-AO">
                    <div className="chat-product-AO__media">
                        {pictures.length ? (
                            <>
                                <div className="chat-product-AO__photo">
                                    <img src={pictures[Math.min(photo, pictures.length - 1)]} alt={name} />
                                </div>
                                {pictures.length > 1 ? (
                                    <div className="chat-product-AO__thumbs">
                                        {pictures.map((src, index) => (
                                            <button
                                                key={src}
                                                type="button"
                                                className={index === photo ? "is-active" : ""}
                                                onClick={() => setPhoto(index)}
                                                aria-label={`Фото ${index + 1}`}
                                            >
                                                <img src={src} alt="" loading="lazy" />
                                            </button>
                                        ))}
                                    </div>
                                ) : null}
                            </>
                        ) : (
                            <div className="chat-product-AO__photo chat-product-AO__photo--empty">Фото нет</div>
                        )}
                    </div>

                    <div className="chat-product-AO__info">
                        <div className="chat-product-AO__chips">
                            {product.article != null ? <span>Арт. {product.article}</span> : null}
                            {product.categoryName ? <span>{product.categoryName}</span> : null}
                            {product.has3D ? <span className="is-accent">3D-модель</span> : null}
                        </div>

                        <section className="chat-product-AO__block">
                            <h4>Цены по камню</h4>
                            {stones.length ? (
                                <div className="chat-product-AO__stones">
                                    {stones.map((stone) => (
                                        <div key={stone.key || stone.name} className="chat-product-AO__stone">
                                            <b>{stone.name || "Камень"}</b>
                                            <div className="chat-product-AO__stone-prices">
                                                <span className="is-subscription">
                                                    <small>Подписка</small>
                                                    {money(stone.subscriberPrice) || "—"}
                                                </span>
                                                <span>
                                                    <small>Обычная</small>
                                                    {money(stone.publicPrice) || "—"}
                                                </span>
                                            </div>
                                            {stone.millingPrice > 0 ? (
                                                <div className="chat-product-AO__milling">
                                                    Фрезеровка отдельно: <b>{money(stone.millingPrice)}</b>
                                                </div>
                                            ) : null}
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <p className="chat-product-AO__muted">Цены для этого товара ещё не посчитаны.</p>
                            )}
                            {sizes.length ? (
                                <p className="chat-product-AO__muted">
                                    {sizes.length > 1 ? `Цены за базовый размер. Есть размеры: ${sizes.join(", ")}` : `Размер: ${sizes[0]}`}
                                </p>
                            ) : null}
                        </section>

                        {facts.length ? (
                            <dl className="chat-product-AO__facts">
                                {facts.map(([label, value]) => (
                                    <React.Fragment key={label}>
                                        <dt>{label}</dt>
                                        <dd>{value}</dd>
                                    </React.Fragment>
                                ))}
                            </dl>
                        ) : null}

                        {product.description ? (
                            <section className="chat-product-AO__block">
                                <h4>Описание</h4>
                                <p className="chat-product-AO__description">{product.description}</p>
                            </section>
                        ) : null}
                    </div>
                </div>
            ) : null}
        </ChatDialog>
    );
}
